import { useState, useCallback, useMemo } from 'react';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from '../constants/attachments';
import { FileUploadStatus, FileUploadError } from './types';
import { useTicketThread } from '../data/useTicketThread';
import { FILE_MANAGER_BASE_PATH } from '../../api/paths';
import { getUploadTokenRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/AttachmentsRpc';
import { chirpClient } from '../utils/chirpClient';
const validateFile = (file, noThread) => {
  let isValid = true;
  let error;
  if (file.size > MAX_FILE_SIZE) {
    isValid = false;
    error = FileUploadError.SIZE_OVERFLOW_ERROR;
  }
  if (!ACCEPTED_FILE_TYPES.some(type => {
    if (type.includes('*')) {
      const mainType = type.split('/')[0];
      return file.type.startsWith(`${mainType}/`);
    }
    return file.type === type;
  })) {
    isValid = false;
    error = FileUploadError.FILE_TYPE_NOT_SUPPORTED;
  }
  if (noThread) {
    isValid = false;
    error = FileUploadError.NO_THREAD;
  }
  return {
    isValid,
    error
  };
};
export function useFileUpload(ticketId, threadId) {
  const {
    lastMessageId
  } = useTicketThread(ticketId, threadId);
  const [fileRecord, setFileRecord] = useState({});
  const files = useMemo(() => Object.values(fileRecord), [fileRecord]);
  const uploadFiles = useCallback(async fileToUpload => {
    const noThread = !lastMessageId;
    const newEntries = {};
    const {
      isValid,
      error
    } = validateFile(fileToUpload, noThread);
    const localId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    newEntries[localId] = {
      file: fileToUpload,
      localId,
      status: isValid ? FileUploadStatus.PENDING : FileUploadStatus.ERROR,
      progress: 0,
      error,
      previewUrl: fileToUpload.type.startsWith('image/') ? URL.createObjectURL(fileToUpload) : undefined
    };
    setFileRecord(prev => Object.assign({}, prev, newEntries));
    const validEntries = Object.values(newEntries).filter(({
      status
    }) => status === FileUploadStatus.PENDING);
    if (validEntries.length === 0 || threadId === undefined) {
      return;
    }
    try {
      const {
        uploadUrl
      } = await chirpClient.call(getUploadTokenRpc, {
        request: {
          params: {
            ticketId,
            threadId
          }
        }
      });
      const formData = new FormData();
      validEntries.forEach(({
        file
      }) => {
        formData.append('files[]', file);
      });
      const request = new XMLHttpRequest();
      request.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const progress = Math.round(event.loaded / event.total * 100);
          setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload) => {
            acc[upload.localId] = Object.assign({}, upload, {
              status: FileUploadStatus.UPLOADING,
              progress
            });
            return acc;
          }, {})));
        }
      });
      request.addEventListener('load', () => {
        if (request.status >= 200 && request.status < 300) {
          const {
            objects: uploadedObjects
          } = JSON.parse(request.responseText);
          setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload, index) => {
            const loadedUpload = uploadedObjects[index];
            acc[upload.localId] = Object.assign({}, upload, {
              status: FileUploadStatus.UPLOADED,
              id: loadedUpload.id,
              downloadUrl: loadedUpload.url,
              expiresAt: loadedUpload.expires_at,
              fileName: loadedUpload.name,
              fileExtension: loadedUpload.extension,
              fileType: loadedUpload.type,
              fileSize: loadedUpload.size,
              imageHeight: loadedUpload.height,
              imageWidth: loadedUpload.width
            });
            return acc;
          }, {})));
        } else {
          const res = JSON.parse(request.response);
          const errorTypeKey = res.errorType;
          const errorType = FileUploadError[errorTypeKey];
          if (!errorType && res.errorType) {
            setTimeout(() => {
              throw new Error(`Unknown file upload error type received from server: ${res.errorType}`);
            }, 0);
          }
          const serverError = errorType !== null && errorType !== void 0 ? errorType : FileUploadError.UPLOAD_FAILED;
          setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload) => {
            acc[upload.localId] = Object.assign({}, upload, {
              status: FileUploadStatus.ERROR,
              error: serverError
            });
            return acc;
          }, {})));
        }
      });
      request.addEventListener('abort', () => {
        setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload) => {
          acc[upload.localId] = Object.assign({}, upload, {
            status: FileUploadStatus.ERROR,
            error: FileUploadError.UPLOAD_ABORTED
          });
          return acc;
        }, {})));
      });
      request.addEventListener('error', uploadError => {
        if (uploadError.target instanceof XMLHttpRequest) {
          const {
            status
          } = uploadError.target;
          if (status >= 400 && status < 500) {
            setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload) => {
              acc[upload.localId] = Object.assign({}, upload, {
                status: FileUploadStatus.ERROR,
                error: FileUploadError.UPLOAD_FAILED
              });
              return acc;
            }, {})));
          }
        }
      });
      const uploadUrlPath = new URL(`${FILE_MANAGER_BASE_PATH}/${uploadUrl}`, window.location.origin);
      request.open('POST', uploadUrlPath.toString(), true);
      request.setRequestHeader('X-HS-User-Request', '1');
      request.send(formData);
    } catch (err) {
      setFileRecord(prev => Object.assign({}, prev, validEntries.reduce((acc, upload) => {
        acc[upload.localId] = Object.assign({}, upload, {
          status: FileUploadStatus.ERROR,
          error: FileUploadError.UPLOAD_FAILED
        });
        return acc;
      }, {})));
    }
  }, [ticketId, threadId, lastMessageId]);
  const removeFile = useCallback(localId => {
    setFileRecord(prev => Object.values(prev).reduce((acc, file) => {
      if (file.localId !== localId) {
        acc[file.localId] = file;
      } else {
        if (file.previewUrl && file.file.type.startsWith('image/')) {
          URL.revokeObjectURL(file.previewUrl);
        }
      }
      return acc;
    }, {}));
  }, []);
  const clearFiles = useCallback(() => {
    setFileRecord(prev => {
      Object.values(prev).forEach(file => {
        if (file.previewUrl && file.file.type.startsWith('image/')) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      return {};
    });
  }, []);
  return {
    files,
    uploadFiles,
    removeFile,
    clearFiles
  };
}